// FeedbackInline — docked thumbs with inline reason chips on thumbs-down.
//
// Per designer handoff Q5: docked (not hover-only — hover-only is invisible
// on touch and to keyboard users). Thumbs-down expands INLINE beneath the
// reply with 3 reason chips (Off topic / Too generic / Missed my context)
// + optional note. No popover or drawer — the reason is about THIS reply
// and should stay next to it. "Missed my context" doubles as a memory-
// quality signal.

import { useState } from 'react';
import { useI18n } from '../i18n/index.js';
import styles from './Coach.module.css';

export type FeedbackVerdict = 'up' | 'down' | null;
export type FeedbackReason = 'off_topic' | 'too_generic' | 'missed_context';

export interface FeedbackInlineProps {
  verdict: FeedbackVerdict;
  onVerdict: (v: FeedbackVerdict) => void;
  /** Called when the user submits a thumbs-down with a reason/note. */
  onSubmitReason: (reason: FeedbackReason | null, note: string) => void | Promise<void>;
}

export function FeedbackInline({
  verdict,
  onVerdict,
  onSubmitReason,
}: FeedbackInlineProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState<FeedbackReason | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  async function handleSend() {
    setSending(true);
    try {
      await onSubmitReason(reason, note.trim());
    } finally {
      setSending(false);
    }
  }

  function toggle(v: 'up' | 'down') {
    const next = verdict === v ? null : v;
    onVerdict(next);
    if (next !== 'down') {
      setReason(null);
      setNote('');
    }
  }

  return (
    <div className={styles.fbWrap}>
      <div className={styles.fb}>
        <button
          type="button"
          className={`${styles.fbb} ${verdict === 'up' ? styles.fbbOn : ''}`}
          aria-label={t('coach.fb.helpful')}
          onClick={() => toggle('up')}
        >
          <ThumbUp />
        </button>
        <button
          type="button"
          className={`${styles.fbb} ${verdict === 'down' ? styles.fbbOn : ''}`}
          aria-label={t('coach.fb.not')}
          onClick={() => toggle('down')}
        >
          <ThumbDown />
        </button>
        {verdict === 'up' && <span className={styles.fbThanks}>{t('coach.fb.thanks')}</span>}
      </div>
      {verdict === 'down' && (
        <div className={styles.fbx}>
          <div className={styles.lbl}>{t('coach.fb.title')}</div>
          <div className={styles.chips}>
            {([
              ['off_topic', 'coach.fb.r1'],
              ['too_generic', 'coach.fb.r2'],
              ['missed_context', 'coach.fb.r3'],
            ] as const).map(([r, k]) => (
              <button
                key={r}
                type="button"
                className={`${styles.chp} ${reason === r ? styles.chpOn : ''}`}
                onClick={() => setReason(reason === r ? null : r)}
              >
                {t(k)}
              </button>
            ))}
          </div>
          <textarea
            className={styles.inp}
            style={{ marginTop: 9, minHeight: 60 }}
            placeholder={t('coach.fb.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className={styles.fbxActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnBrand}`}
              onClick={handleSend}
              disabled={sending}
            >
              {t('coach.fb.send')}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={() => onVerdict(null)}
              disabled={sending}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ThumbUp() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 11v9H4v-9h3zm5-8l-.5 2a4 4 0 00-.5 2v2h5a2 2 0 012 2l-2 7a2 2 0 01-2 1H9V11l3-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ThumbDown() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ transform: 'rotate(180deg)' }}
    >
      <path
        d="M7 11v9H4v-9h3zm5-8l-.5 2a4 4 0 00-.5 2v2h5a2 2 0 012 2l-2 7a2 2 0 01-2 1H9V11l3-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
